import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UsersService } from '../utils/apiClient';
import { authUtils } from '../utils/auth';

export const SignupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [email, setEmail] = useState('');
  const [emailPrefilled, setEmailPrefilled] = useState(false);
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No signup token provided');
        setValidatingToken(false);
        return;
      }

      try {
        const data = await UsersService().validateSignupTokenUsersValidateSignupTokenGet(token);
        setEmail(data.email);
        setEmailPrefilled(true);
        setError('');
      } catch (err: any) {
        setError(err.body?.detail || 'Invalid signup token');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('Invalid signup link');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await UsersService().registerUserUsersRegisterPost(
        token,
        {
          email,
          username: username || null,
          full_name: fullName || null,
          password,
        }
      );

      if (response.refresh_token) {
        authUtils.setUserTokens(response.token, response.refresh_token);
      } else {
        authUtils.setUserToken(response.token);
      }
      navigate('/');
    } catch (err: any) {
      setError(err.body?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-h4ks-dark-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="border-2 border-h4ks-green-700 bg-h4ks-dark-900 p-8">
            <h1 className="text-2xl font-bold text-h4ks-green-400 mb-6 font-mono">
              [h4kstream / signup]
            </h1>
            <div className="text-gray-400 text-center py-8">
              Validating signup token...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-h4ks-dark-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="border-2 border-h4ks-green-700 bg-h4ks-dark-900 p-8">
          <h1 className="text-2xl font-bold text-h4ks-green-400 mb-6 font-mono">
            [h4kstream / signup]
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Email * {emailPrefilled && <span className="text-xs text-gray-500">(pre-filled)</span>}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={emailPrefilled}
                required
                className="w-full bg-h4ks-dark-800 border border-h4ks-green-800 text-gray-300 px-3 py-2 focus:outline-none focus:border-h4ks-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-h4ks-dark-800 border border-h4ks-green-800 text-gray-300 px-3 py-2 focus:outline-none focus:border-h4ks-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-h4ks-dark-800 border border-h4ks-green-800 text-gray-300 px-3 py-2 focus:outline-none focus:border-h4ks-green-500"
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Password *</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-h4ks-dark-800 border border-h4ks-green-800 text-gray-300 px-3 py-2 focus:outline-none focus:border-h4ks-green-500"
              />
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-700 text-red-400 px-3 py-2 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              className="w-full bg-h4ks-green-700 hover:bg-h4ks-green-600 text-white font-mono py-2 px-4 transition-colors disabled:opacity-50"
            >
              {loading ? '[CREATING ACCOUNT...]' : '[SIGNUP]'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full border border-h4ks-green-800 hover:border-h4ks-green-600 text-gray-400 hover:text-gray-300 font-mono py-2 px-4 transition-colors"
            >
              [BACK TO HOME]
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
